/**
 * cmHomeApi — fetch + hook for the CM home aggregate read
 * (GET /api/t/{slug}/v1/home?today=YYYY-MM-DD).
 *
 * ONE fetch per page open (clientRecordApi precedent: one read = one
 * phi_access_log row server-side). Wire types hand-mirror
 * api/_shared/repos/cmHome.ts (the FE cannot import api/_shared —
 * separate package; the capabilities.ts / noteVocabulary.ts mirror
 * discipline).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import type { SpiceHoldReason, SpiceSessionFrequency, SpiceSessionKind } from '@/lib/spiceVisitPlansApi';
import type { ClientListRowWire } from '@/lib/clientsApi';
import { practiceTodayIso } from '@/lib/practiceDate';

export interface CmHomeRiskFlagWire {
  category: string;
  level: 'elevated';
}

export interface CmHomeClientWire {
  id: string;
  display_name: string;
  /** clients.pronouns (the 0128 closed set); null = not recorded. The
   *  writing tools inherit it when a care manager picks the client from her
   *  caseload. */
  pronouns: string | null;
  /** true when the viewer is the client's lead care manager; false for an
   *  additional care manager, a SPICE client or a care-team add. */
  is_primary: boolean;
  /** 0142 hint: a photo is on file (the roster's has_photo precedent) —
   *  the band lazily loads it from the serve endpoint; never the bytes. */
  has_photo: boolean;
  risk_flags: CmHomeRiskFlagWire[];
  visit_today: boolean;
  /** 0218: the earliest timed visit today ('HH:MM'); null when none has a
   *  time. Optional for an older cached shape. */
  visit_today_time?: string | null;
  note_count: number;
  has_assessment: boolean;
  last_note_occurred_on: string | null;
  last_touched_at: string | null;
  /** LP-11 (My Clients): the structured name; null for a client with only
   *  a display name. The list orders by display_name (LP-19); the first and
   *  preferred names title the visit dialog. Optional for an older cached
   *  shape. */
  first_name?: string | null;
  last_name?: string | null;
  preferred_name?: string | null;
  /** LP-11: the earliest scheduled visit today or later; null when none. */
  next_visit?: { date: string; time: string | null } | null;
  /** The earliest care visit (a care manager's planned visit) today or
   *  later, by any care manager; null when none. Optional for an older
   *  cached shape. */
  next_care_visit?: { date: string; time: string | null } | null;
  /** LP-11: the record chip for where the client is staying. */
  stay?: 'hospital' | 'hospice' | null;
  /** True on a row where the VIEWER is the client's own assigned or
   *  covering SPICE therapist: every row for a SPICE-only viewer (her
   *  whole caseload IS that set), or the subset of a dual-role viewer's
   *  caseload she also covers as therapist. Absent (undefined) on every
   *  other row, including a plain care manager's entire caseload. */
  is_spice_client?: boolean;
  /** True when this client's engagement is 'spice' — no care-management
   *  side at all. Present on every row, either viewer. */
  is_spice_only_client: boolean;
  /** True for a demo or training client: "New client" and the assessment
   *  nudge never show on such a row. */
  is_demo: boolean;
  /** Present on every row for a viewer who is NOT SPICE-only — an
   *  ordinary care manager, or a dual-role viewer's own SPICE-client rows
   *  alike: the caseload's most recent non-voided SPICE-band note (any
   *  author); null when none exists yet; absent on a SPICE-only viewer's
   *  own path. Rendered only on a row with no spice_row — a row carrying
   *  the template says its SPICE story there (cmHome.ts lastActivityLine). */
  last_spice_note?: { occurred_on: string; declined: boolean } | null;
  /** The unified SPICE row template's facts, present on every row
   *  (either viewer) whose client has an assigned SPICE therapist; null
   *  when the client carries no assigned therapist at all. */
  spice_row: CmHomeSpiceRowWire | null;
}

export interface CmHomeSpiceRowWire {
  therapist_name: string;
  /** The plan's state on the practice day: active, covered, or a pause's
   *  word (on_hold, vacation, hospital). */
  status: 'active' | 'covered' | 'on_hold' | 'vacation' | 'hospital';
  /** The reason, note and until: null while the plan reads regular. */
  hold_reason: SpiceHoldReason | null;
  hold_note: string | null;
  /** ISO date; null while the plan reads regular. */
  status_until: string | null;
  /** The covering therapist's name while the plan reads covered; absent
   *  or null otherwise. */
  cover_name?: string | null;
  /** The nearest upcoming occurrence; null while paused (a covered plan's
   *  visits continue with the cover), or when no session resolves within
   *  the lookahead window. therapist_name is set when that session has a
   *  therapist of its own. */
  next_session: { date: string; weekday: number; start_time: string; therapist_name?: string } | null;
}

// ---------------------------------------------------------------------
// SPICE Train A2 — "My week" (present for any viewer whose own roster
// row is does_spice — a SPICE-only therapist or a dual-role viewer who
// also does care management).
// ---------------------------------------------------------------------

export type CmHomeMyWeekVisitState = 'due' | 'visited';

export interface CmHomeMyWeekVisitWire {
  client_id: string;
  client_display_name: string;
  start_time: string; // 'HH:MM'
  /** The session's kind, SPICE or Friendly Visitor. */
  kind: SpiceSessionKind;
  /** Minutes for a Friendly Visitor session; null for SPICE. */
  duration_minutes: number | null;
  frequency: SpiceSessionFrequency;
  /** Until Train B2: the last SPICE-band note's plan line; null when none
   *  exists yet. B2 replaces this with the confirmed next-plan. */
  last_plan: string | null;
  state: CmHomeMyWeekVisitState;
}

export interface CmHomeMyWeekDayWire {
  date: string; // 'YYYY-MM-DD'
  weekday: number; // 1..7, Mon..Sun
  visits: CmHomeMyWeekVisitWire[];
}

export interface CmHomeMyWeekNotThisWeekWire {
  client_id: string;
  client_display_name: string;
  /** covered while another therapist covers the visits; a pause's word
   *  (on_hold, vacation, hospital) while paused. */
  status: 'covered' | 'on_hold' | 'vacation' | 'hospital';
  hold_reason: SpiceHoldReason;
  hold_note: string | null;
  status_until: string | null;
  /** The covering therapist's name while covered; null while paused. */
  cover_name: string | null;
}

export interface CmHomeMyWeekWire {
  week_start: string;
  week_end: string;
  /** Monday..Sunday, chronological: the FE reorders today-first and
   *  shows a weekend day only when a visit falls on it
   *  (src/lib/myWeek.ts orderMyWeekDays). */
  days: CmHomeMyWeekDayWire[];
  not_this_week: CmHomeMyWeekNotThisWeekWire[];
}

export type CmHomeAttentionKind = 'confirm_updates' | 'needs_detail' | 'extraction_failed';

export interface CmHomeAttentionWire {
  kind: CmHomeAttentionKind;
  note_id: string;
  client_id: string;
  client_display_name: string;
  note_kind: 'note' | 'assessment';
  note_occurred_on: string;
  anchor_at: string;
  type_counts: Partial<Record<string, number>> | null;
  pending_count: number | null;
  hours: string | null;
  /** Her own note on a client she can no longer open (confirm_updates and
   *  extraction_failed): the card asks the office and offers no button.
   *  Absent reads as in scope. */
  out_of_scope?: boolean;
}

/** Unit-scope search entry: names/navigation only, never row facts. */
export interface CmHomeUnitClientWire {
  id: string;
  display_name: string;
}

export type CmHomeRailKind = 'direct_edit' | 'confirmed_updates' | 'note_posted' | 'intake_seed';

export interface CmHomeRailLineWire {
  id: string;
  kind: CmHomeRailKind;
  actor_name: string;
  actor_role: string | null;
  client_id: string;
  client_display_name: string;
  entity: string | null;
  count: number;
  from_your_note_occurred_on: string | null;
  at: string;
}

/** A planned visit day this week that has already passed with no
 *  SPICE-band note for that client — never present for a care manager. */
export interface CmHomeSpiceAttentionWire {
  kind: 'session_missing_note';
  client_id: string;
  client_display_name: string;
  /** The planned visit's calendar date (ISO), already passed. */
  visit_on: string;
  /** Carried along so the item's door (the Email helper, same as My
   *  week's own) seeds "Email Content" like the visit it names. */
  last_plan: string | null;
}

/** One care visit dated today on a client of the caseload. */
export interface CmHomeCareVisitTodayWire {
  id: string;
  client_id: string;
  client_display_name: string;
  /** 'HH:MM' or null. */
  time: string | null;
  care_manager_name: string;
  /** The visit is the viewer's own. */
  is_mine: boolean;
}

/** LP-24 Coming Up: one appointment, care visit or SPICE session in the
 *  next three days (api/_shared/comingUpShape.ts). */
export interface CmHomeComingUpLineWire {
  key: string;
  date: string;
  /** 'HH:MM' or null. */
  time: string | null;
  client_id: string;
  client_display_name: string;
  kind: 'appointment' | 'care_visit' | 'spice';
  /** A care visit's care manager; null for the other kinds. */
  care_manager_name: string | null;
  /** A care visit is the viewer's own; false for the other kinds. */
  is_mine: boolean;
}

export interface CmHomeSnapshot {
  today: string;
  clients: CmHomeClientWire[];
  attention: CmHomeAttentionWire[];
  rail: CmHomeRailLineWire[];
  visits_today_count: number;
  /** Care visits dated today on the caseload's clients, earliest first.
   *  Optional for an older cached shape. */
  care_visits_today?: CmHomeCareVisitTodayWire[];
  /** LP-24: today, tomorrow and the day after, date then time order.
   *  Optional for an older cached shape (Coming Up then reads today's
   *  lines from the fields above). */
  coming_up?: CmHomeComingUpLineWire[];
  /**
   * Active clients in the caller's visibility unit minus the caseload —
   * the home search's wider scope (founder walk 2026-07-06). Empty when
   * the caller has no unit set (search falls back to the caseload).
   */
  unit_clients: CmHomeUnitClientWire[];
  /** SPICE Train A2: present for any viewer whose own roster row is
   *  does_spice; null for a care-management-only viewer, or a viewer
   *  with no roster row at all. */
  my_week: CmHomeMyWeekWire | null;
  /** Derived from my_week: empty wherever my_week is null. */
  spice_attention: CmHomeSpiceAttentionWire[];
}

export function cmHomeKey(slug: string, today: string): readonly unknown[] {
  return ['cm-home', slug, today] as const;
}

/** Every day's home read for the tenant: what a write that changes the
 *  home's facts (a care visit saved or removed) invalidates. */
export function cmHomeTenantKey(slug: string): readonly unknown[] {
  return ['cm-home', slug] as const;
}

async function fetchCmHome(
  slug: string,
  today: string,
  signal?: AbortSignal,
): Promise<CmHomeSnapshot> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/home?today=${encodeURIComponent(today)}`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `GET ${path} -> HTTP ${res.status}`);
  }
  const payload = (await res.json()) as { home: CmHomeSnapshot };
  return payload.home;
}

export interface UseCmHomeResult {
  home: CmHomeSnapshot | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useCmHome(
  slug: string,
  today: string,
  opts: { enabled?: boolean } = {},
): UseCmHomeResult {
  const query = useQuery<CmHomeSnapshot>({
    queryKey: cmHomeKey(slug, today),
    queryFn: ({ signal }) => fetchCmHome(slug, today, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '' && today !== '',
  });
  return {
    home: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * A line seat's own caseload as the writing tools' client lookup rows: id,
 * display name and recorded pronouns. The home read for the practice's day
 * (the home page's today), so every writing tool shares its cache entry. undefined when
 * not enabled (the seat looks up all clients instead).
 */
export function useOwnCaseloadPicks(
  tenantSlug: string,
  enabled: boolean,
): Array<Pick<ClientListRowWire, 'id' | 'display_name' | 'pronouns'>> | undefined {
  const today = useMemo(() => practiceTodayIso(), []);
  const { home } = useCmHome(tenantSlug, today, { enabled });
  return useMemo(
    () =>
      enabled
        ? (home?.clients ?? []).map((c) => ({ id: c.id, display_name: c.display_name, pronouns: c.pronouns }))
        : undefined,
    [enabled, home],
  );
}
